/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import Ic from '../Icon';
import { $ } from '../theme';

// ── 24-hour module-level cache (keyed by ZIP) ─────────────────────────────────
const CACHE = {};
const CACHE_TTL = 24 * 60 * 60 * 1000;

// ── DFW city → ZIP fallback map ───────────────────────────────────────────────
const DFW_CITY_ZIP = {
  'lewisville': '75067', 'fort worth': '76102', 'dallas': '75201',
  'plano': '75024', 'frisco': '75034', 'mckinney': '75070',
  'allen': '75013', 'denton': '76201', 'irving': '75038',
  'garland': '75040', 'arlington': '76010', 'grand prairie': '75050',
  'mesquite': '75149', 'carrollton': '75006', 'richardson': '75080',
  'rowlett': '75088', 'flower mound': '75028', 'mansfield': '76063',
  'euless': '76039', 'bedford': '76021', 'hurst': '76053',
  'grapevine': '76051', 'southlake': '76092', 'colleyville': '76034',
  'keller': '76248', 'north richland hills': '76180', 'waxahachie': '75165',
  'cedar hill': '75104', 'desoto': '75115', 'duncanville': '75116',
  'lancaster': '75134', 'rockwall': '75087', 'wylie': '75098',
  'sachse': '75048', 'murphy': '75094', 'prosper': '75078',
  'celina': '75009', 'little elm': '75068', 'the colony': '75056',
  'aubrey': '76227', 'argyle': '76226', 'coppell': '75019',
  'farmers branch': '75234', 'addison': '75001',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function extractZip(property) {
  if (!property) return null;
  if (property.zip_code) return String(property.zip_code).trim();
  if (property.zip)      return String(property.zip).trim();
  const addr = String(property.address || property.addr || '');
  const m = addr.match(/\b(7[5-6]\d{3})\b/);
  if (m) return m[1];
  const haystack = addr.toLowerCase();
  for (const [city, zip] of Object.entries(DFW_CITY_ZIP)) {
    if (haystack.includes(city)) return zip;
  }
  return null;
}

async function fetchMarket(zip) {
  if (!zip) return { data: null, error: null };
  const cached = CACHE[zip];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return { data: cached.data, error: null };
  try {
    const res = await fetch('/api/rentcast', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ zipCode: zip }),
    });
    if (!res.ok) {
      let msg = 'RentCast error ' + res.status;
      try { const e = await res.json(); if (e.error) msg = e.error; } catch {}
      return { data: null, error: msg };
    }
    const data = await res.json();
    CACHE[zip] = { ts: Date.now(), data };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Network error: ' + err.message };
  }
}

function getDemand(days) {
  if (days == null || days <= 0) return { label: '—', color: '#8b949e' };
  if (days < 20)  return { label: 'High',   color: '#22c55e' };
  if (days <= 35) return { label: 'Medium', color: '#d29922' };
  return             { label: 'Low',    color: '#f85149' };
}

function getAssessment(currentRent, marketAvg) {
  if (!marketAvg || !currentRent) return { label: 'No Data', color: '#8b949e', pct: 0 };
  const pct = ((currentRent - marketAvg) / marketAvg) * 100;
  if (pct >  5) return { label: 'Overpriced',  color: '#f85149', pct };
  if (pct < -5) return { label: 'Underpriced', color: '#58a6ff', pct };
  return           { label: 'Well-Priced',  color: '#22c55e', pct };
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function RentPricing({ properties = [] }) {
  const [activeTab,     setActiveTab]     = useState('portfolio');
  const [marketData,    setMarketData]    = useState({});
  const [loading,       setLoading]       = useState({});
  const [zipLookup,     setZipLookup]     = useState('');
  const [lookupData,    setLookupData]    = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError,   setLookupError]   = useState('');

  // Prefetch market data for all properties
  useEffect(() => {
    const seen = new Set();
    properties.forEach(async prop => {
      const zip = extractZip(prop);
      if (!zip || seen.has(zip)) return;
      seen.add(zip);
      if (marketData[zip]) return;
      setLoading(prev => ({ ...prev, [zip]: true }));
      const { data } = await fetchMarket(zip);
      setMarketData(prev => ({ ...prev, [zip]: data }));
      setLoading(prev => ({ ...prev, [zip]: false }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.length]);

  const handleLookup = async () => {
    const z = zipLookup.trim();
    if (!/^\d{5}$/.test(z)) { setLookupError('Enter a valid 5-digit ZIP code.'); return; }
    setLookupError('');
    setLookupLoading(true);
    setLookupData(null);
    const { data, error } = await fetchMarket(z);
    if (error) setLookupError(error);
    else if (!data) setLookupError('No data returned for this ZIP.');
    else setLookupData(data);
    setLookupLoading(false);
  };

  // ── Main UI ───────────────────────────────────────────────────────────────
  return (
    <div>

      {/* DFW Market Overview Banner */}
      <div style={{ background: 'linear-gradient(135deg,#161b22,#1c2333)', border: '1px solid #21262d', borderRadius: 14, padding: '16px 22px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Ic n="trend" s={16} c="#22c55e"/>
          <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontWeight: 700, color: '#fff' }}>DFW Market Overview</span>
          <span style={{ fontSize: 10, color: '#484f58', background: '#21262d', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Current</span>
        </div>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          {[
            { l: 'Metro Median', v: '$1,783' },
            { l: 'SFR Median',   v: '$2,195' },
            { l: 'Avg Days',     v: '27 days' },
            { l: 'Fastest Market', v: 'Allen TX · 9d' },
          ].map(s => (
            <div key={s.l} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 900, color: '#4ade80' }}>{s.v}</div>
              <div style={{ fontSize: 10, color: '#8b949e', textTransform: 'uppercase', letterSpacing: 0.7, marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab${activeTab === 'portfolio' ? ' tab-on' : ''}`} onClick={() => setActiveTab('portfolio')}>
          My Portfolio
        </button>
        <button className={`tab${activeTab === 'lookup' ? ' tab-on' : ''}`} onClick={() => setActiveTab('lookup')}>
          ZIP Code Lookup
        </button>
      </div>

      {/* ── Portfolio Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'portfolio' && (
        <div>
          {properties.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '44px 24px', color: '#484f58' }}>
              <Ic n="build" s={38} c="#484f58"/>
              <div style={{ marginTop: 14, fontSize: 13 }}>No properties added yet.</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>Add properties in the Property Tracker tab to see pricing analysis.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {properties.map(prop => {
                const zip         = extractZip(prop);
                const mkt         = zip ? marketData[zip] : null;
                const isLoading   = zip ? !!loading[zip]  : false;
                const currentRent = prop.monthly_rent || prop.rent || 0;
                const marketAvg   = mkt?.averageRent || mkt?.medianRent || null;
                const daysToLease = mkt?.averageDaysOnMarket ?? mkt?.daysOnMarket ?? null;
                const demand      = getDemand(daysToLease);
                const assessment  = getAssessment(currentRent, marketAvg);
                const sugLow  = marketAvg ? Math.round(marketAvg * 0.95) : null;
                const sugHigh = marketAvg ? Math.round(marketAvg * 1.05) : null;
                const upside  = (marketAvg && currentRent && currentRent < marketAvg)
                  ? Math.round(marketAvg - currentRent) : 0;
                const maxScale  = marketAvg ? marketAvg * 1.25 : 3000;
                const yourBarW  = currentRent ? Math.min(100, (currentRent / maxScale) * 100) : 0;
                const mktBarW   = marketAvg   ? Math.min(100, (marketAvg   / maxScale) * 100) : 0;

                return (
                  <div key={prop.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    {/* Colour accent bar */}
                    <div style={{ height: 4, background: assessment.color }}/>

                    <div style={{ padding: '18px 20px' }}>
                      {/* Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: '#fff' }}>
                            {prop.name || prop.address || 'Property'}
                          </div>
                          <div style={{ fontSize: 12, color: '#8b949e', marginTop: 3 }}>
                            {prop.address || prop.addr || ''}
                            {zip ? <span style={{ color: '#484f58' }}> · ZIP {zip}</span> : <span style={{ color: '#d29922' }}> · No ZIP detected</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 20, background: assessment.color + '22', color: assessment.color }}>
                            {isLoading ? '…' : assessment.label}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 20, background: demand.color + '22', color: demand.color }}>
                            {isLoading ? '…' : demand.label + ' Demand'}
                          </span>
                        </div>
                      </div>

                      {/* Stats grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
                        {[
                          { l: 'Your Rent',      v: currentRent ? $(currentRent) : '—',   c: '#f5f0e8' },
                          { l: 'Market Avg',     v: isLoading ? '…' : marketAvg ? $(marketAvg) : '—', c: '#4ade80' },
                          { l: 'Suggested Range',v: sugLow && sugHigh ? `${$(sugLow)}–${$(sugHigh)}` : '—', c: '#58a6ff', sm: true },
                          { l: 'Days to Lease',  v: isLoading ? '…' : daysToLease != null ? `${daysToLease}d` : '—', c: demand.color },
                        ].map(s => (
                          <div key={s.l} style={{ background: '#21262d', borderRadius: 8, padding: '10px 12px' }}>
                            <div style={{ fontSize: 10, color: '#8b949e', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 4 }}>{s.l}</div>
                            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: s.sm ? 12 : 17, fontWeight: 700, color: s.c, lineHeight: 1.2 }}>{s.v}</div>
                          </div>
                        ))}
                      </div>

                      {/* Pricing bar — only when we have market data */}
                      {marketAvg > 0 && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8b949e', marginBottom: 6 }}>
                            <span>Your rate vs. market</span>
                            <span style={{ color: assessment.color, fontWeight: 700 }}>
                              {assessment.pct !== 0 ? `${assessment.pct > 0 ? '+' : ''}${assessment.pct.toFixed(1)}% vs market` : 'At market rate'}
                            </span>
                          </div>
                          {/* Market reference line */}
                          <div style={{ position: 'relative', height: 10, background: '#21262d', borderRadius: 5, overflow: 'visible', marginBottom: 4 }}>
                            {/* Market avg track */}
                            <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${mktBarW}%`, background: '#2d333b', borderRadius: 5 }}/>
                            {/* Your rent bar */}
                            <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${yourBarW}%`, background: assessment.color, borderRadius: 5, transition: 'width .6s ease' }}/>
                            {/* Market avg tick */}
                            <div style={{ position: 'absolute', top: -3, left: `${mktBarW}%`, width: 2, height: 16, background: '#8b949e', borderRadius: 1, transform: 'translateX(-50%)' }}/>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#484f58' }}>
                            <span>$0</span>
                            <span style={{ color: '#8b949e' }}>Market avg: {$(marketAvg)}</span>
                            <span>{$(Math.round(maxScale))}</span>
                          </div>
                        </div>
                      )}

                      {/* Upside callout */}
                      {upside > 0 && (
                        <div style={{ background: 'rgba(34,197,94,.07)', border: '1px solid rgba(34,197,94,.18)', borderRadius: 8, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Ic n="trend" s={13} c="#22c55e"/>
                          <span style={{ fontSize: 12, color: '#4ade80' }}>
                            Potential upside: <strong>{$(upside)}/mo</strong> · <strong>{$(upside * 12)}/yr</strong> if priced at market rate
                          </span>
                        </div>
                      )}

                      {/* No data states */}
                      {!isLoading && zip && !mkt && (
                        <div style={{ fontSize: 12, color: '#484f58', marginTop: 6 }}>
                          No market data returned for ZIP {zip}. RentCast may not have coverage for this area yet.
                        </div>
                      )}
                      {!zip && (
                        <div style={{ background: 'rgba(210,153,34,.07)', border: '1px solid rgba(210,153,34,.2)', borderRadius: 8, padding: '8px 12px', marginTop: 6 }}>
                          <span style={{ fontSize: 12, color: '#d29922' }}>
                            Add a ZIP code or DFW city address to this property to enable market comparison.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── ZIP Code Lookup Tab ────────────────────────────────────────────── */}
      {activeTab === 'lookup' && (
        <div>
          <div className="card">
            <div className="card-hd"><h3>DFW ZIP Code Market Lookup</h3></div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div className="fi" style={{ flex: 1, marginBottom: 0 }}>
                <label>ZIP Code</label>
                <input
                  value={zipLookup}
                  onChange={e => setZipLookup(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  placeholder="e.g. 75034"
                  style={{ fontFamily: 'monospace', letterSpacing: 3 }}
                  maxLength={5}
                />
              </div>
              <button className="btn btn-g" onClick={handleLookup} disabled={lookupLoading} style={{ marginBottom: 1 }}>
                <Ic n="trend" s={13}/> {lookupLoading ? 'Loading…' : 'Look Up'}
              </button>
            </div>
            {lookupError && (
              <div style={{ fontSize: 12, color: '#f85149', marginTop: 8 }}>{lookupError}</div>
            )}
          </div>

          {lookupData && (
            <div className="card" style={{ marginTop: 14, padding: 0, overflow: 'hidden' }}>
              <div style={{ height: 4, background: '#22c55e' }}/>
              <div style={{ padding: '18px 20px' }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 900, color: '#fff', marginBottom: 4 }}>
                  ZIP {zipLookup}
                  {(lookupData.city || lookupData.zipCode) && (
                    <span style={{ fontSize: 13, fontWeight: 400, color: '#8b949e', marginLeft: 10 }}>
                      {lookupData.city || ''}{lookupData.state ? `, ${lookupData.state}` : ''}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#484f58', marginBottom: 18 }}>6-month historical range · RentCast</div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                  {[
                    { l: 'Average Rent',    v: lookupData.averageRent   != null ? $(lookupData.averageRent)                           : '—', c: '#4ade80' },
                    { l: 'Median Rent',     v: lookupData.medianRent    != null ? $(lookupData.medianRent)                            : '—', c: '#4ade80' },
                    { l: 'Days on Market',  v: lookupData.averageDaysOnMarket != null ? `${lookupData.averageDaysOnMarket}d`          : '—', c: getDemand(lookupData.averageDaysOnMarket).color },
                    { l: 'Rent Range Low',  v: lookupData.minRent != null ? $(lookupData.minRent)                                    : '—', c: '#f5f0e8' },
                    { l: 'Rent Range High', v: lookupData.maxRent != null ? $(lookupData.maxRent)                                    : '—', c: '#f5f0e8' },
                    { l: 'Active Listings', v: lookupData.totalListings != null ? String(lookupData.totalListings)                   : '—', c: '#58a6ff' },
                  ].map(s => (
                    <div key={s.l} style={{ background: '#21262d', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ fontSize: 10, color: '#8b949e', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5 }}>{s.l}</div>
                      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 900, color: s.c }}>{s.v}</div>
                    </div>
                  ))}
                </div>

                {(lookupData.averageRent || lookupData.medianRent) && (
                  <div style={{ background: 'rgba(34,197,94,.07)', border: '1px solid rgba(34,197,94,.18)', borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8 }}>Suggested Pricing Range (±5%)</div>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 900, color: '#4ade80' }}>
                      {$(Math.round((lookupData.averageRent || lookupData.medianRent) * 0.95))}
                      <span style={{ color: '#484f58', fontWeight: 400, fontSize: 18, margin: '0 10px' }}>–</span>
                      {$(Math.round((lookupData.averageRent || lookupData.medianRent) * 1.05))}
                    </div>
                    <div style={{ fontSize: 11, color: '#8b949e', marginTop: 4 }}>per month · based on average rent for {zipLookup}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick-access DFW ZIP grid */}
          <div className="card" style={{ marginTop: 14 }}>
            <div className="card-hd"><h3>Common DFW ZIP Codes</h3></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
              {Object.entries(DFW_CITY_ZIP).map(([city, zip]) => (
                <button
                  key={zip}
                  className="btn btn-gh"
                  style={{ justifyContent: 'space-between', fontSize: 11, padding: '6px 10px' }}
                  onClick={() => { setZipLookup(zip); }}
                >
                  <span style={{ textTransform: 'capitalize' }}>{city}</span>
                  <span style={{ fontFamily: 'monospace', color: '#58a6ff', letterSpacing: 1 }}>{zip}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
